import { useEffect, useState } from "react";
import type { ComponentType } from "react";
import { RotateCw } from "lucide-react";

/**
 * Loads an interactive tool widget on the client only.
 *
 * The tool pages are prerendered (SSR) so their editorial content is
 * indexable. The interactive widgets, however, touch browser-only APIs
 * (Excalidraw, localStorage, WebGPU, Notification…) and must never run
 * during SSR. This wrapper renders a sized placeholder on the server and
 * the first client render, then dynamically imports the real widget after
 * mount — the same shape as pages/Demo.tsx, generalized.
 *
 * If the dynamic import fails — most often when a stale tab requests a hashed
 * chunk that no longer exists after a deploy — we surface a retry instead of
 * spinning on "Loading…" forever.
 */
export function LazyWidget({
  load,
  minHeight = 480,
}: {
  load: () => Promise<{ default: ComponentType }>;
  minHeight?: number;
}) {
  const [Widget, setWidget] = useState<ComponentType | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    load()
      .then((m) => {
        if (!cancelled) setWidget(() => m.default);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [load, attempt]);

  if (Widget) return <Widget />;

  if (failed) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 text-center px-6"
        style={{ minHeight }}
      >
        <p className="text-sm text-slate-600">This tool failed to load.</p>
        <button
          onClick={() => {
            setWidget(null);
            setAttempt((a) => a + 1);
          }}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors"
        >
          <RotateCw size={14} /> Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
        >
          Or reload the page
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-sm text-slate-500"
      style={{ minHeight }}
    >
      Loading tool…
    </div>
  );
}
